import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

import getSearchResults from "api/requests/getSearchResults";
import { SearchPageTypes } from "custom-types/Search";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import {
  DEFAULT_SEARCH_SKIP,
  DEFAULT_SEARCH_TAKE,
  MAX_DISTANCE,
  SEARCH_LOAD_MORE,
  SEARCH_LOADING,
  SEARCH_RESULTS,
  SearchLoadingAction,
  SearchLoadMoreAction,
  SearchResultsAction,
} from "redux/reducers/search";
import { getCoordinates } from "redux/selectors/location";
import {
  getIsNullResult,
  getSearchCategoryRelevanceOrder,
  getSearchData,
  getSearchLoading,
  getSearchMenuItemCategories,
  getSearchMenuItemDispensaries,
} from "redux/selectors/search";

const useSearch = () => {
  const {
    query: { q, searchCategory },
  } = useRouter();

  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();
  const coordinates = useSelector(getCoordinates);
  const loading = useSelector(getSearchLoading);
  const results = useSelector(getSearchData);
  const menuItemCategories = useSelector(getSearchMenuItemCategories);
  const menuItemDispensaries = useSelector(getSearchMenuItemDispensaries);
  const categoryRelevanceOrder = useSelector(getSearchCategoryRelevanceOrder);
  const isNullResult = useSelector(getIsNullResult);

  const getResults = async (
    take = DEFAULT_SEARCH_TAKE,
    skip = DEFAULT_SEARCH_SKIP,
    boost = "",
    skipAggs = false
  ) => {
    dispatch<SearchLoadingAction>({
      type: SEARCH_LOADING,
    });

    // * Adding a max distance to the menu items piece of global search.
    // * https://leafly.atlassian.net/browse/SHH-438
    const menuItemMaxDistanceFilter = {
      "filter[menu_items_max_distance]": "300",
    };

    const {
      article,
      brand,
      dispensary,
      menuItem,
      strain,
      menuItemCategories,
      menuItemDispensaries,
      categoryRelevanceOrder,
    } = await getSearchResults(
      { "filter[type]": searchCategory, q, ...menuItemMaxDistanceFilter },
      coordinates,
      MAX_DISTANCE,
      countryCode,
      take,
      skip,
      boost,
      skipAggs
    );

    dispatch<SearchResultsAction>({
      results: {
        article,
        brand,
        categoryRelevanceOrder,
        dispensary,
        menuItem,
        menuItemCategories,
        menuItemDispensaries,
        strain,
      },
      type: SEARCH_RESULTS,
    });
  };

  const loadMore = async (type: SearchPageTypes, viewingCount: number) => {
    const numberToLoad = viewingCount - results[type].length;

    if (numberToLoad > 0) {
      const additionalItems = await getSearchResults(
        { "filter[type]": type, q },
        coordinates,
        MAX_DISTANCE,
        countryCode,
        numberToLoad,
        results[type].length
      );

      const newResults = [...results[type], ...additionalItems[type]];

      dispatch<SearchLoadMoreAction>({
        result: { [type]: newResults },
        type: SEARCH_LOAD_MORE,
      });
    }
  };

  return {
    loading,
    ...results,
    categoryRelevanceOrder,
    getSearchResults: getResults,
    loadMoreSearchResults: loadMore,
    maxDistance: MAX_DISTANCE,
    menuItemCategories,
    menuItemDispensaries,
    nullResults: isNullResult,
    searchTerm: q,
  };
};

export default useSearch;
